import { useRef } from "react";

import {
    AppleLogo,
    GoogleLogo,
    MicrosoftLogo,
    SlackLogo,
    useAnalytics,
    useAnalyticsAttributes,
    useLanguage
} from "../../components";
import { UiSocialLoginSignupButtonModelContent } from "../../mocks";
import {
    UiSocialLoginSignupButtonModel,
    UiLoginSignupFormModel
} from "../../models";
import { getCookie } from "../../utils";

import { Button, ButtonProps } from "./UiSocialLoginSignupButton.styles";

export type UiSocialLoginSignupButtonProps = {
    readonly model: UiSocialLoginSignupButtonModel;
    readonly buttonType?: UiLoginSignupFormModel["content"]["formType"];
    readonly appearance?: ButtonProps["appearance"];
};

const UiSocialLoginSignupButton = ({
    model,
    buttonType = "signup",
    appearance = "shadow"
}: UiSocialLoginSignupButtonProps) => {
    const {
        id,
        content: { buttonText, entryTitle, providerName }
    } = model;
    const currentLanguage = useLanguage();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    const dscInputRef = useRef<HTMLInputElement | null>(null);

    const setDscValueOnSubmit = () => {
        if (dscInputRef.current) {
            dscInputRef.current.value = getCookie("dsc") ?? "";
        }
    };

    return (
        <form
            action="/authenticate_openid"
            method="post"
            onSubmit={setDscValueOnSubmit}
        >
            <input type="hidden" name="provider" value="atlassian" />
            <input type="hidden" name="subProvider" value={providerName} />
            <input type="hidden" name="tosAccepted" value="true" />
            <input type="hidden" name="locale" value={currentLanguage} />
            <input
                name="dsc"
                data-testid="dsc-input"
                type="hidden"
                value=""
                ref={dscInputRef}
            />
            <Button
                id={providerName} // Used in AAAA team Pollinator tests
                appearance={appearance}
                onClick={() => {
                    const eventName = `clickedSocial${
                        buttonType === "login" ? "Login" : "Signup"
                    }Button`;
                    const eventComponents = {
                        microsoft: "signUpWithMicrosoftButton",
                        google: "signUpWithGmailButton", // Preserve "Gmail" button naming (BXPT-1362)
                        apple: "signUpWithAppleButton",
                        slack: "signUpWithSlackButton"
                    };
                    sendTrackEvent(eventName, {
                        ...analyticsAttributes,
                        event: "clicked",
                        eventComponent: eventComponents[providerName],
                        label: buttonText,
                        labelEnglish: entryTitle,
                        uuid: id
                    });
                }}
                type="submit"
            >
                {getIcon(providerName)}
                {buttonText}
            </Button>
        </form>
    );
};

function getIcon(
    provider: UiSocialLoginSignupButtonModelContent["providerName"]
) {
    switch (provider) {
        case "google":
            return <GoogleLogo />;
        case "microsoft":
            return <MicrosoftLogo />;
        case "apple":
            return <AppleLogo />;
        case "slack":
            return <SlackLogo />;
    }
}

export default UiSocialLoginSignupButton;
